import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Copy, Pencil } from "lucide-react";
import { mutate } from "swr";
import { DropdownMenuItem } from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import {
  EntityOverviewHeader,
  OverviewTile,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { OverflowMenu } from "~/pages/manifold-nextgen/components/OverflowMenu";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanCustomizeAgentAppearance } from "~/pages/manifold-nextgen/hooks/useCanCustomizeAgentAppearance";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import {
  Agent,
  AgentAccessLevel,
  AgentScope,
  agentPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import { AgentAccessBadge } from "./AgentAccessBadge";
import { AgentAvatar } from "./AgentAvatar";
import { AgentEditDialog, agentEditTitle } from "./AgentEditDialog";

interface AgentOverviewTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const AgentOverviewTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentOverviewTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const isEditable = !sessionSidebarView && canEditGrant;
  const canCustomizeAppearanceGrant = useCanCustomizeAgentAppearance(agent);
  const canCustomizeAppearance = !sessionSidebarView && canCustomizeAppearanceGrant;
  const canEditSomething = isEditable || canCustomizeAppearance;
  const [editOpen, setEditOpen] = useState(false);
  const isSystemAgent = agent.scope === AgentScope.SYSTEM;
  const { showSuccess, showError } = useToast();

  const { collaborators, accessLevels, collaboratorsError } = useAgentShare(agent.id);

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(agent.id);
      showSuccess("Agent ID copied");
    } catch {
      showError("Failed to copy agent ID");
    }
  };
  const { isFavorite, toggleFavorite } = useFavorites("agent-favorites", [agent], (a) => a.id);

  const owners = useOwners(collaborators, accessLevels, AgentAccessLevel.OWNER, collaboratorsError);

  const tiles: OverviewTile[] = [
    {
      label: "Role",
      // `access_level` is the caller's resolved level — at least USER for a SYSTEM
      // agent via its `user:*` grant, higher for a directly-granted collaborator.
      content: <AgentAccessBadge accessLevel={agent.access_level} />,
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} emptyLabel="—" />,
    },
    {
      label: "Type",
      content: isSystemAgent ? "System" : "Custom",
    },
  ];

  const handleSaveDescription = async (next: string) => {
    try {
      await updateAgent(agent.id, { description: next });
      // `description` shows on the list card, so refresh the lists too.
      await Promise.all([mutate(agentPath(agent.id)), mutateAgentList()]);
    } catch (e) {
      showError("Failed to update description");
      throw e;
    }
  };

  // The sidebar shows the tiles alone — the page it sits beside already names
  // the agent and carries its controls.
  const header = sessionSidebarView
    ? {}
    : {
        icon: <AgentAvatar agent={agent} size={48} />,
        title: agent.display_name,
        type: "Agent",
        updatedAt: agent.updated_at,
        actions: (
          <>
            <FavoriteButton
              favorite={isFavorite(agent.id)}
              onToggle={() => toggleFavorite(agent.id)}
              outlined
            />
            <OverflowMenu label="agent actions">
              {canEditSomething && (
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  <Pencil className="size-4" aria-hidden="true" />
                  {agentEditTitle(isEditable, canCustomizeAppearance)}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => void handleCopyId()}>
                <Copy className="size-4" aria-hidden="true" />
                Copy agent ID
              </DropdownMenuItem>
            </OverflowMenu>
          </>
        ),
      };

  return (
    <div className="mx-auto w-full max-w-3xl pt-4">
      <EntityOverviewHeader
        tiles={tiles}
        tileLayout={sessionSidebarView ? "stacked" : "strip"}
        {...header}
      >
        <EditableDescription
          heading="Description"
          value={agent.description}
          canEdit={isEditable}
          onSave={handleSaveDescription}
        />
      </EntityOverviewHeader>

      {canEditSomething && editOpen && (
        <AgentEditDialog
          agent={agent}
          open
          onOpenChange={setEditOpen}
          canRename={isEditable}
          canRestyle={canCustomizeAppearance}
        />
      )}
    </div>
  );
};
